import { useAuth } from "@mydailydirect/auth";
import { AppShell, ErrorBoundary, scrollToTop, type NavItem } from "@mydailydirect/ui";
import { type ReactNode, useLayoutEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

export interface RouterShellProps {
  appName: string;
  subtitle?: string;
  nav: NavItem[];
  bottomNav?: NavItem[];
  showBottomNavOnDesktop?: boolean;
  cartBadgeCount?: number;
  onCartClick?: () => void;
  /** Explicitly override whether to show the header back button */
  canGoBack?: boolean;
  /** Explicitly override the back button click handler */
  onBack?: () => void;
  /** Optional custom max-width / container classes for the main content area */
  contentClassName?: string;
  children: ReactNode;
}

/**
 * AppShell wired to react-router NavLink + the current auth session.
 * Automatically resets viewport & scroll container to the top on every route change.
 * Supports desktop sidebar, mobile bottom nav, header cart icon, and safe back navigation.
 */
export function RouterShell({
  appName,
  subtitle,
  nav,
  bottomNav,
  showBottomNavOnDesktop = false,
  cartBadgeCount,
  onCartClick,
  canGoBack,
  onBack,
  contentClassName,
  children,
}: RouterShellProps) {
  const { session, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Root screen has no back button; any inner route automatically has one
  const isRoot = location.pathname === "/" || location.pathname === "";
  const defaultCanGoBack = !isRoot;

  // Safe back navigation: go to previous page if within app history, else go to root
  const defaultHandleGoBack = () => {
    if (
      typeof window !== "undefined" &&
      window.history?.state &&
      typeof window.history.state.idx === "number" &&
      window.history.state.idx > 0
    ) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  // Scroll to top on ANY route navigation across the entire app
  useLayoutEffect(() => {
    scrollToTop();
    const rafId = requestAnimationFrame(() => {
      scrollToTop();
    });
    return () => cancelAnimationFrame(rafId);
  }, [location.pathname, location.search]);

  return (
    <AppShell
      appName={appName}
      subtitle={subtitle}
      nav={nav}
      bottomNav={bottomNav}
      showBottomNavOnDesktop={showBottomNavOnDesktop}
      cartBadgeCount={cartBadgeCount}
      onCartClick={onCartClick}
      canGoBack={canGoBack ?? defaultCanGoBack}
      onBack={onBack ?? defaultHandleGoBack}
      contentClassName={contentClassName}
      user={
        session
          ? {
              name: session.fullName,
              tenant: session.tenantId,
              phone: session.phone || session.phoneNumber,
              orgId: session.orgId || session.organizationId,
              role: session.roles?.[0] || session.accountType,
              accountType: session.accountType,
            }
          : undefined
      }
      onLogout={logout}
      renderNavLink={(item, className, isCollapsed) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.href === "/"}
            className={className}
            title={item.label}
            onClick={item.onClick}
          >
            {Icon && <Icon className={isCollapsed ? "h-5 w-5 shrink-0" : "h-4 w-4 shrink-0"} />}
            {!isCollapsed && <span className="truncate">{item.label}</span>}
            {item.badge != null && (
              <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-extrabold text-primary-foreground">
                {item.badge}
              </span>
            )}
          </NavLink>
        );
      }}
      renderBottomNavLink={(item, className) => {
        const Icon = item.icon;
        if (item.onClick && !item.href) {
          return (
            <button
              key={item.label}
              type="button"
              onClick={item.onClick}
              className={className}
              title={item.label}
            >
              {Icon && <Icon className="h-5 w-5 shrink-0" />}
              <span className="max-w-full truncate px-0.5 text-[10px] leading-none font-medium">{item.label}</span>
              {item.badge != null && Number(item.badge) > 0 && (
                <span className="absolute top-1 right-1/4 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-extrabold text-primary-foreground">
                  {item.badge}
                </span>
              )}
            </button>
          );
        }
        return (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.href === "/"}
            className={className}
            title={item.label}
          >
            {Icon && <Icon className="h-5 w-5 shrink-0" />}
            <span className="max-w-full truncate px-0.5 text-[10px] leading-none font-medium">{item.label}</span>
            {item.badge != null && Number(item.badge) > 0 && (
              <span className="absolute top-1 right-1/4 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-1 text-[9px] font-extrabold text-primary-foreground">
                {item.badge}
              </span>
            )}
          </NavLink>
        );
      }}
    >
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
    </AppShell>
  );
}
